import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Unlink
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const MenuButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, isActive, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`
      p-1 rounded transition-colors duration-200
      ${isActive
        ? 'bg-blue-500/30 text-blue-400'
        : 'text-foreground-tertiary hover:text-foreground-primary hover:bg-white/10'
      }
      disabled:opacity-50 disabled:cursor-not-allowed
    `}
  >
    {children}
  </button>
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  className = '',
  id
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-blue-400 underline hover:text-blue-300',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none focus:outline-none min-h-[80px] px-3 py-2',
        id: id || '',
      },
    },
  });

  const setLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    let url = window.prompt('URL do link:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Add https:// if no protocol is provided
    if (url && !url.match(/^https?:\/\//i) && !url.startsWith('mailto:')) {
      url = 'https://' + url;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={`rich-text-editor ${className}`}>
      {/* Toolbar */}
      <div className={`
        flex items-center gap-0.5 px-1.5 py-1
        border border-white/30 border-b-0 rounded-t-lg
        bg-white/5 backdrop-blur-sm
        ${disabled ? 'opacity-50 pointer-events-none' : ''}
      `}>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          disabled={disabled}
          title="Negrito (Ctrl+B)"
        >
          <Bold className="w-3.5 h-3.5" />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          disabled={disabled}
          title="Itálico (Ctrl+I)"
        >
          <Italic className="w-3.5 h-3.5" />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          disabled={disabled}
          title="Sublinhado (Ctrl+U)"
        >
          <UnderlineIcon className="w-3.5 h-3.5" />
        </MenuButton>

        <div className="w-px h-3.5 bg-white/20 mx-0.5" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          disabled={disabled}
          title="Lista com marcadores"
        >
          <List className="w-3.5 h-3.5" />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          disabled={disabled}
          title="Lista numerada"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </MenuButton>

        <div className="w-px h-3.5 bg-white/20 mx-0.5" />

        <MenuButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          disabled={disabled}
          title="Adicionar link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </MenuButton>

        {editor.isActive('link') && (
          <MenuButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={disabled}
            title="Remover link"
          >
            <Unlink className="w-3.5 h-3.5" />
          </MenuButton>
        )}
      </div>

      {/* Editor Content */}
      <div className={`
        bg-white/5 border border-white/30 rounded-b-lg
        text-foreground-primary
        focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/30
        hover:border-white/40
        transition-all duration-200
        backdrop-blur-sm
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}>
        <EditorContent
          editor={editor}
          className="[&_.ProseMirror]:min-h-[80px] [&_.ProseMirror]:max-h-[300px] [&_.ProseMirror]:overflow-y-auto"
        />
        {!value && placeholder && (
          <div className="absolute top-2 left-3 text-muted-foreground pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

// Utility to sanitize HTML for safe rendering
export const sanitizeHtml = (html: string): string => {
  if (typeof window === 'undefined') return html;

  // Dynamic import for SSR compatibility
  const DOMPurify = require('dompurify');
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });
};

// Component to render sanitized HTML content
export const RichTextContent: React.FC<{
  content: string;
  className?: string;
}> = ({ content, className = '' }) => {
  if (!content) return null;

  const sanitizedContent = sanitizeHtml(content);

  return (
    <div
      className={`rich-text-content prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};
